import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import { curveCardinal } from "d3";
import { lightFormat } from "date-fns";
import { toPng } from "html-to-image";
import { useCallback, useMemo, useRef, useState } from "react";
import { useMedia } from "react-use";
import { Area, AreaChart, Tooltip as RechartsTooltip, ResponsiveContainer, TooltipProps, XAxis } from "recharts";
import useSWR from "swr";
import type { Address } from "viem";
import { parseUnits } from "viem";

import { USD_DECIMALS } from "config/factors";
import { useShowDebugValues } from "context/SyntheticsStateContext/hooks/settingsHooks";
import type { FromOldToNewArray } from "domain/tradingview/types";
import { getAccountPnlHistory } from "lib/apis/accounts";
import { SECONDS_IN_DAY, formatDate, formatDateTime, getTimestampByDaysAgo } from "lib/dates";
import downloadImage from "lib/downloadImage";
import { helperToast } from "lib/helperToast";
import { bigintToNumber, formatUsd } from "lib/numbers";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "lib/objects";
import { getPositiveOrNegativeClass } from "lib/utils";

import Loader from "components/Common/Loader";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import { ButtonRowScrollFadeContainer } from "components/TableScrollFade/TableScrollFade";
import Tabs from "components/Tabs/Tabs";

import {
  DEBUG_FIELDS,
  DebugLines,
  DebugTooltip,
  type AccountPnlHistoryPointDebugFields,
} from "./dailyAndCumulativePnLDebug";

import "./DailyAndCumulativePnL.css";

const CHART_TOOLTIP_WRAPPER_STYLE: React.CSSProperties = { zIndex: 10000 };

const CHART_TICK_PROPS: React.SVGProps<SVGTextElement> = { fill: "var(--color-gray-lighter)" };
const PNL_CHART_DOT = { r: 0 };
const PNL_CHART_ACTIVE_DOT = {
  r: 4,
  stroke: "#FFFFFF",
  strokeWidth: 2,
  fill: "#0FCF8A",
};

// export function DailyAndCumulativePnL({ chainId, account }: { chainId: number; account: Address }) {
//   const [fromDate, setFromDate] = useState<Date | undefined>(getInitialDate);
//   const fromTimestamp = useMemo(() => fromDate && toUtcDayStart(fromDate), [fromDate]);

//   const { data: clusteredPnlData, error, loading } = usePnlHistoricalData(chainId, account, fromTimestamp);

//   const { cardRef, handleImageDownload } = useImageDownload();

//   return (
//     <div className="flex flex-col rounded-8 bg-gray-darker" ref={cardRef}>
//       <div className="flex items-center justify-between border-b border-b-gray-950 px-16">
//         <div className="text-body-large py-8">
//           <Trans>Daily and Cumulative PnL</Trans>
//         </div>
//         <div className="flex flex-wrap items-stretch justify-end gap-8 py-8">
//           <Button variant="secondary" data-exclude slim imgSrc={downloadIcon} onClick={handleImageDownload}>
//             PNG
//           </Button>
//           <DateSelect date={fromDate} onChange={setFromDate} buttonTextPrefix={t`From`} />
//         </div>
//       </div>

//       <div className="flex flex-wrap gap-24 px-16 pt-16 text-gray-lighter">
//         <div>
//           <div className="inline-block size-10 rounded-full bg-green-500" /> <Trans>Daily Profit</Trans>
//         </div>
//         <div>
//           <div className="inline-block size-10 rounded-full bg-red-500" /> <Trans>Daily Loss</Trans>
//         </div>
//         <div>
//           <div className="inline-block size-10 rounded-full bg-[#468AE3]" />{" "}
//           <Trans>
//             Cumulative PnL:{" "}
//             <span className={getPositiveOrNegativeClass(clusteredPnlData.at(-1)?.cumulativePnl)}>
//               {formatUsd(clusteredPnlData.at(-1)?.cumulativePnl)}
//             </span>
//           </Trans>
//         </div>
//         <DebugLegend lastPoint={clusteredPnlData.at(-1)} />
//       </div>

//       <div className="relative min-h-[250px] grow">
//         <div className="DailyAndCumulativePnL-hide-last-tick absolute size-full">
//           <ResponsiveContainer debounce={500}>
//             <ComposedChart width={500} height={300} data={clusteredPnlData} barGap={0}>
//               <RechartsTooltip content={ChartTooltip} wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE} />
//               <Bar dataKey="pnlFloat" minPointSize={1}>
//                 {clusteredPnlData.map(renderPnlBar)}
//               </Bar>
//               <Line type="monotone" dataKey="cumulativePnlFloat" stroke="#468AE3" strokeWidth={2} dot={false} />
//               <XAxis
//                 dataKey="dateCompact"
//                 axisLine={false}
//                 tickLine={false}
//                 angle={-90}
//                 fontSize={12}
//                 tickMargin={25}
//                 height={50}
//                 dx={-4}
//                 minTickGap={10}
//                 tick={CHART_TICK_PROPS}
//               />
//               <YAxis
//                 mirror
//                 type="number"
//                 allowDecimals={false}
//                 markerWidth={0}
//                 tickMargin={-6}
//                 axisLine={false}
//                 tickLine={false}
//                 tickFormatter={yAxisTickFormatter}
//                 tick={CHART_TICK_PROPS}
//               />
//               {DebugLines()}
//             </ComposedChart>
//           </ResponsiveContainer>
//         </div>
//         {error && (
//           <div className="absolute grid size-full max-h-full place-items-center overflow-auto">
//             <div className="whitespace-pre-wrap font-mono text-red-alpha-100">{JSON.stringify(error, null, 2)}</div>
//           </div>
//         )}
//         {loading && (
//           <div className="absolute grid size-full place-items-center">
//             <Loader />
//           </div>
//         )}
//         {!loading && !error && clusteredPnlData.length === 0 && (
//           <div className="absolute grid size-full place-items-center text-gray-lighter">
//             <Trans>No data available</Trans>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

type PnlTimeframe = "24h" | "7d" | "14d" | "30d" | "all";

const pnlTimeframeOptions: { value: PnlTimeframe; label: string }[] = [
  { value: "24h", label: "24H" },
  { value: "7d", label: "7D" },
  { value: "14d", label: "14D" },
  { value: "30d", label: "30D" },
  { value: "all", label: "ALL" },
];

export function PnlOverview({ chainId, account }: { chainId: number; account?: Address }) {
  const [activeTimeframe, setActiveTimeframe] = useState<PnlTimeframe>("24h");

  const fromTimestamp = useMemo(() => {
    if (activeTimeframe === "all") return undefined;
    if (activeTimeframe === "30d") return getTimestampByDaysAgo(30);
    if (activeTimeframe === "14d") return getTimestampByDaysAgo(14);
    if (activeTimeframe === "7d") return getTimestampByDaysAgo(7);
    return getTimestampByDaysAgo(1);
  }, [activeTimeframe]);
  const { data: clusteredPnlData, error, loading } = usePnlHistoricalData(chainId, account, fromTimestamp, undefined);

  const isMobile = useMedia("(max-width: 480px)");
  const { cardRef } = useImageDownload();

  const curveFunction = curveCardinal.tension(0.01);

  return (
    <div className="flex flex-col rounded-8 bg-gray-darker" ref={cardRef}>
      <div className="flex flex-col items-start px-16 sm:flex-row sm:items-center sm:justify-between">
        <div className="py-12 text-14 font-medium leading-[1] text-white sm:py-18 sm:text-[20px]">
          <Trans>PNL overview</Trans>
        </div>
        <ButtonRowScrollFadeContainer>
          <Tabs
            options={pnlTimeframeOptions.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            selectedValue={activeTimeframe}
            onChange={setActiveTimeframe}
            variant="filled"
            size={isMobile ? "small" : "medium"}
            type="inline"
            className="w-max"
            regularOptionClassname="!min-w-52"
          />
        </ButtonRowScrollFadeContainer>
      </div>

      {/* <div className="flex flex-wrap gap-24 px-16 pt-16 text-gray-lighter">
        <div>
          <div className="inline-block size-10 rounded-full bg-green-500" /> <Trans>Daily Profit</Trans>
        </div>
        <div>
          <div className="inline-block size-10 rounded-full bg-red-500" /> <Trans>Daily Loss</Trans>
        </div>
        <div>
          <div className="inline-block size-10 rounded-full bg-[#468AE3]" />{" "}
          <Trans>
            Cumulative PnL:{" "}
            <span className={getPositiveOrNegativeClass(clusteredPnlData.at(-1)?.cumulativePnl)}>
              {formatUsd(clusteredPnlData.at(-1)?.cumulativePnl)}
            </span>
          </Trans>
        </div>
        <DebugLegend lastPoint={clusteredPnlData.at(-1)} />
      </div> */}

      <div
        className={cx("relative min-h-[250px] w-full grow", {
          "min-h-[160px]": isMobile,
        })}
      >
        <div className="DailyAndCumulativePnL-hide-last-tick absolute size-full px-14 sm:px-18 md:px-24 lg:px-80">
          <ResponsiveContainer height={isMobile ? 160 : 250} width="100%">
            {/* <AreaChart width={500} height={300} data={clusteredPnlData} barGap={0}> */}
            <AreaChart data={clusteredPnlData} barGap={0}>
              <defs>
                <linearGradient id={`color-positive`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0FCF8A" stopOpacity={0.4}></stop>
                  <stop offset="75%" stopColor="#0FCF8A" stopOpacity={0.2}></stop>
                  <stop offset="100%" stopColor="#0FCF8A" stopOpacity={0}></stop>
                </linearGradient>
              </defs>
              <RechartsTooltip content={ChartTooltip} wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE} />
              <Area
                type={curveFunction}
                dataKey="cumulativePnlFloat"
                stroke="#0FCF8A"
                fill="url(#color-positive)"
                strokeWidth={3}
                dot={PNL_CHART_DOT}
                activeDot={PNL_CHART_ACTIVE_DOT}
                animationEasing="ease-in-out"
                animationDuration={750}
                animateNewValues={true}
              />
              <XAxis
                dataKey="dateCompact"
                axisLine={false}
                tickLine={false}
                // angle={-90}
                fontSize={10}
                tickMargin={-5}
                // height={50}
                // dx={-4}
                minTickGap={16}
                tick={CHART_TICK_PROPS}
              />
              {/* <YAxis
                mirror
                type="number"
                allowDecimals={false}
                markerWidth={0}
                // tickMargin={-6}
                axisLine={false}
                tickLine={false}
                tickFormatter={yAxisTickFormatter}
                tick={CHART_TICK_PROPS}
              /> */}
              {DebugLines()}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {!account && (
          <div className="absolute grid size-full place-items-center text-gray-lighter">
            <div className="text-center">
              <div className="text-14 font-medium text-white">
                <Trans>Connect Wallet</Trans>
              </div>
              <div className="mt-6 text-12 text-gray-lighter">
                <Trans>Connect your wallet to see your PNL</Trans>
              </div>
            </div>
          </div>
        )}
        {account && error && (
          <div className="absolute grid size-full max-h-full place-items-center overflow-auto">
            <div className="whitespace-pre-wrap font-mono text-red-alpha-100">{JSON.stringify(error, null, 2)}</div>
          </div>
        )}
        {account && loading && (
          <div className="absolute grid size-full place-items-center">
            <Loader />
          </div>
        )}
        {account && !loading && !error && clusteredPnlData.length === 0 && (
          <div className="absolute grid size-full place-items-center text-gray-lighter">
            <Trans>No data available</Trans>
          </div>
        )}
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload }: TooltipProps<number | string, "pnl" | "cumulativePnl" | "date">) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const stats = payload[0].payload as PnlHistoricalData[number];

  return (
    <div className="z-50 rounded-8 border border-gray-dark bg-gray-darker p-8 text-14">
      <StatsTooltipRow label={t`Date`} value={stats.date} showDollar={false} />
      <StatsTooltipRow
        label={t`PnL`}
        value={formatUsd(stats.pnl)}
        showDollar={false}
        textClassName={getPositiveOrNegativeClass(stats.pnl)}
      />
      <StatsTooltipRow
        label={t`Cumulative PnL`}
        value={formatUsd(stats.cumulativePnl)}
        showDollar={false}
        textClassName={getPositiveOrNegativeClass(stats.cumulativePnl)}
      />
      <DebugTooltip stats={stats} />
    </div>
  );
}

export type AccountPnlHistoryPoint = {
  date: string;
  dateCompact: string;
  timestamp: number;
  pnlFloat: number;
  pnl: bigint;
  cumulativePnlFloat: number;
  cumulativePnl: bigint;
} & AccountPnlHistoryPointDebugFields;

type PnlHistoricalData = FromOldToNewArray<AccountPnlHistoryPoint>;

function usePnlHistoricalData(
  chainId: number,
  account: Address | undefined,
  fromTimestamp: number | undefined,
  toTimestamp: number | undefined
) {
  const showDebugValues = useShowDebugValues();
  const normalizedAccount = account?.toLowerCase();
  const { data, error } = useSWR(
    normalizedAccount
      ? ["accountPnlHistory", chainId, normalizedAccount, fromTimestamp, toTimestamp, showDebugValues]
      : null,
    () =>
      getAccountPnlHistory(normalizedAccount!, {
        chainId,
        from: fromTimestamp,
        to: toTimestamp,
        debug: showDebugValues,
      }),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    }
  );

  const transformedData: AccountPnlHistoryPoint[] = useMemo(() => {
    const rows = data?.points ?? [];
    if (rows.length === 0) return EMPTY_ARRAY as AccountPnlHistoryPoint[];

    const points = rows
      .map((row: any) => {
        const timestamp = Number(row.timestamp);
        const pnl = parseUsdString(row.pnl);
        const cumulativePnl = parseUsdString(row.cumulativePnl);
        const parsedDebugFields = showDebugValues
          ? DEBUG_FIELDS.reduce(
              (acc, key) => {
                const bn = parseUsdString(row[key]);
                acc[key] = bn;
                acc[`${key}Float`] = bigintToNumber(bn, USD_DECIMALS);
                return acc;
              },
              {} as Record<string, bigint | number>
            )
          : EMPTY_OBJECT;

        return {
          date: showDebugValues
            ? formatDateTime(timestamp) + " - " + formatDateTime(timestamp + SECONDS_IN_DAY) + " local"
            : formatDate(timestamp),
          dateCompact: lightFormat(timestamp * 1000, "dd/MM"),
          timestamp,
          pnl,
          pnlFloat: bigintToNumber(pnl, USD_DECIMALS),
          cumulativePnl,
          cumulativePnlFloat: bigintToNumber(cumulativePnl, USD_DECIMALS),
          ...parsedDebugFields,
        } as AccountPnlHistoryPoint;
      })
      .filter((point) => Number.isFinite(point.timestamp) && point.timestamp > 0)
      .sort((a, b) => a.timestamp - b.timestamp);

    return points.length ? points : (EMPTY_ARRAY as AccountPnlHistoryPoint[]);
  }, [data?.points, showDebugValues]);

  return { data: transformedData, error, loading: !data && !error };
}

function useImageDownload() {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleImageDownload = useCallback(() => {
    if (!cardRef.current) {
      helperToast.error("Error in downloading image");
      return;
    }

    toPng(cardRef.current, {
      filter: (element: any) => {
        if (element.dataset?.exclude) {
          return false;
        }
        return true;
      },
    }).then((dataUri) => {
      downloadImage(dataUri, "daily-and-cumulative-pnl.png");
    });
  }, []);

  return { cardRef, handleImageDownload };
}

function parseUsdString(value: unknown): bigint {
  if (value === undefined || value === null) return 0n;
  const normalized = String(value).trim();
  if (!normalized || normalized === "0") return 0n;

  if (/^-?\d+$/.test(normalized) && normalized.length > 20) {
    try {
      return BigInt(normalized);
    } catch (_error) {
      return 0n;
    }
  }

  try {
    return parseUnits(normalized as `${number}`, USD_DECIMALS);
  } catch (_error) {
    try {
      return BigInt(normalized);
    } catch (_error) {
      return 0n;
    }
  }
}
